import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { motion } from "motion/react";
import { getHumanReadableTimespan } from "../utils/getHumanReadableTimespan";
import { Subscribed } from "../components/Subscribed";

import type { Tier } from "@commonTypes/tier";
import type {
  CheckSubscriptionResponse,
  SubscriptionDetails,
} from "@commonTypes/checkSubscription";

import "./UserProfile.css";

export const UserProfile: React.FC = () => {
  const { userID } = useParams<{ userID: string }>();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);
  const [subsciberFor, setSubscriberFor] = useState<string | null>(null);
  const [subsciberSince, setSubscriberSince] = useState<string | null>(null);
  const [tier, setTier] = useState<Tier>("basic");

  useEffect(() => {
    const fetchUser = async () => {
      if (!userID) {
        setError("Invalid User ID");
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", userID); // Reference to /users/{userID}
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setUser(userSnap.data());
        } else {
          setError("User not found");
        }
      } catch (err) {
        setError("Failed to fetch user data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userID]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const start = performance.now();

    httpsCallable<
      { appUserId: string; env: string },
      CheckSubscriptionResponse
    >(
      functions,
      "checkSubscription"
    )({ appUserId: userID ?? "", env: "sandbox" })
      .then((res) => {
        const subToQrCode = res.data.details.some(
          (subscription: SubscriptionDetails): boolean => {
            return (
              subscription.status === "active" &&
              subscription.entitlements.some((entitlement) => {
                return entitlement.key === "qr_code_access";
              })
            );
          }
        );

        const activeEntitlements = res.data.details.reduce(
          (acc: Set<string>, subscription) => {
            if (subscription.status === "active") {
              for (const entitlement of subscription.entitlements) {
                acc.add(entitlement.key);
              }
            }
            return acc;
          },
          new Set<string>()
        );
        if (activeEntitlements.has("ultimate")) {
          setTier("ultimate");
        } else if (activeEntitlements.has("rediculous")) {
          setTier("rediculous");
        } else if (activeEntitlements.has("platinum")) {
          setTier("platinum");
        } else if (activeEntitlements.has("gold")) {
          setTier("gold");
        } else if (activeEntitlements.has("qr_code_access")) {
          setTier("basic");
        }

        // Delay the update to show the loading spinner for min 1 second
        setTimeout(() => {
          setIsSubscribed(subToQrCode);
        }, Math.max(0, 1000 - (performance.now() - start)));

        if (subToQrCode) {
          setSubscriberSince(res.data.since ?? new Date().toISOString());
          setSubscriberFor(
            getHumanReadableTimespan(res.data.since ?? new Date().toISOString())
          );
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to check subscription status");
      });
  }, [user, userID]);

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <motion.div
        className="text-center p-4 mt-24 text-3xl font-bold"
        initial={{ scale: 1 }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
        }}
      >
        <div className="text-center p-4 text-red-500">
          <p>{error}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className={`w-full tier tier-${tier}`}>
      <div className="text-center p-4">
        <motion.h1
          className="text-4xl font-bold"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {user?.name}
        </motion.h1>
        <motion.h2
          className="text-2xl mt-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          is
        </motion.h2>
        {user ? (
          <motion.div
            className="text-center text-7xl font-bold mt-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            {isSubscribed === null ? (
              <motion.h2
                className="text-3xl font-bold"
                initial={{ scale: 0.9 }}
                animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                }}
              >
                ...
              </motion.h2>
            ) : (
              <>
                {isSubscribed === true ? (
                  <Subscribed tier={tier} />
                ) : (
                  <motion.div
                    className="text-7xl font-bold"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                    }}
                  >
                    <motion.div
                      className="text-red-500"
                      initial={{ scale: 0.8 }}
                      animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
                      transition={{
                        duration: 0.8,
                        repeat: 2,
                      }}
                    >
                      Not Subscribed
                    </motion.div>
                  </motion.div>
                )}
              </>
            )}
            {isSubscribed === true && (
              <div>
                <motion.p
                  className="text-lg mt-2 text-gray-300"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5, duration: 0.5 }}
                >
                  and had been for {subsciberFor}
                </motion.p>
                <motion.p
                  className="mt-1 text-gray-500 text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.0, duration: 0.5 }}
                >
                  since {new Date(subsciberSince!).toLocaleDateString()} @{" "}
                  {new Date(subsciberSince!).toLocaleTimeString()}
                </motion.p>
              </div>
            )}
          </motion.div>
        ) : (
          <p>No user data available</p>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
